import { coerceServerMessageTypeToUi, normalizeChatMessageFlow } from 'types/chat/messages/common';
import { HistoryMessage, MessageModel } from 'types/chat/messages/message';
import { extractReplyPayloadWithWeb3Fallback } from 'utils/extract-reply-from-api';
import {
  getMessageBodyFromPayload,
  getMessageMediaTypeFromPayload,
  getMessageNumericIdFromPayload,
  getWhatsappMessageIdFromPayload,
} from 'utils/normalize-chat-message-payload';
import { resolveMessageWhatsappErrorCode, resolveStatusHebFromServerStatus, HEBREW_SNACKBAR } from 'utils/whatsappErrors';
import { extractSenderFieldsFromMessagePayload } from 'utils/message-assignee';

/**
 * Row from `GET /api/services/getMessages/...` — extend as backend adds columns.
 * Kept loose so unknown fields pass through via shallow spread.
 */
export type GetMessagesApiRow = Record<string, unknown> & {
  id?: number;
  date?: string;
  status?: string;
  type?: string;
  direction?: string;
  message?: string;
  fileUrl?: string | null;
  reaction?: unknown;
  replyMessage?: unknown;
  sessionId?: string | null;
  formattedMessage?: string | null;
  manager?: { id: string; fullName: string } | null;
  imported?: boolean | null;
  recovered?: boolean | null;
  isForwarded?: boolean | null;
};

export function convertGetMessagesToHistoryMessage(message: GetMessagesApiRow): HistoryMessage {
  const m = message as Record<string, unknown>;

  const base = { ...m };
  delete base.direction;
  delete base.Direction;

  const directionRaw = (m.direction ?? m.Direction ?? m.Type ?? m.type) as string | undefined;
  const statusRaw = m.status ?? m.Status;
  const statusStr = typeof statusRaw === 'string' ? statusRaw : String(statusRaw ?? '');
  const dateRaw = m.date ?? m.Date;
  const dateStr = typeof dateRaw === 'string' ? dateRaw : String(dateRaw ?? '');
  const fileUrlRaw = m.fileUrl ?? m.FileUrl;
  const waMsgId = getWhatsappMessageIdFromPayload(m);
  const reply = extractReplyPayloadWithWeb3Fallback(m);
  const mediaType = getMessageMediaTypeFromPayload(m);
  const numericId = getMessageNumericIdFromPayload(m) ?? (typeof m.id === 'number' ? m.id : undefined);

  const statusHebFromPayloadRaw =
    (typeof m.statusHeb === 'string' ? m.statusHeb : null) ??
    (typeof m.StatusHeb === 'string' ? m.StatusHeb : null);
  const statusHebFromPayload =
    statusHebFromPayloadRaw && statusHebFromPayloadRaw.trim() !== HEBREW_SNACKBAR.genericError
      ? statusHebFromPayloadRaw
      : null;

  const statusHebResolved = statusHebFromPayload ?? resolveStatusHebFromServerStatus(statusStr);

  const whatsappErrorCodeResolved = resolveMessageWhatsappErrorCode({
    Status: statusStr,
    StatusHeb: statusHebFromPayload ?? statusHebResolved,
    whatsappErrorCode:
      (m.whatsappErrorCode as string | number | null | undefined) ??
      (m.WhatsappErrorCode as string | number | null | undefined) ??
      null,
  });

  const formatted =
    (m.formattedMessage as string | null | undefined) ??
    (m.FormattedMessage as string | null | undefined) ??
    undefined;

  const objectRaw = m.Object ?? m.object;
  const objectValue =
    typeof objectRaw === 'string'
      ? objectRaw
      : objectRaw != null && typeof objectRaw === 'object'
        ? JSON.stringify(objectRaw)
        : null;

  //* כרטיס תצוגה מקדימה (link-preview / קמפיין CTWA). השרת מחזיר מחרוזת JSON;
  //* מנרמלים גם למקרה שמגיע כאובייקט — LinkifiedText עושה JSON.parse ומצפה למחרוזת.
  const websitePreviewRaw = m.WebsitePreviewObj ?? m.websitePreviewObj;
  const websitePreviewValue =
    typeof websitePreviewRaw === 'string'
      ? websitePreviewRaw
      : websitePreviewRaw != null && typeof websitePreviewRaw === 'object'
        ? JSON.stringify(websitePreviewRaw)
        : null;

  return {
    ...base,
    Id: numericId ?? null,
    Type: normalizeChatMessageFlow(directionRaw),
    Message: getMessageBodyFromPayload(m),
    ...(waMsgId ? { MessageId: waMsgId, messageId: waMsgId } : {}),
    FileType: mediaType as HistoryMessage['FileType'],
    File:
      (typeof fileUrlRaw === 'string' ? fileUrlRaw : null) ??
      (typeof message.fileUrl === 'string' ? message.fileUrl : null) ??
      null,
    Status: statusStr as HistoryMessage['Status'],
    StatusHeb: statusHebResolved,
    ...(whatsappErrorCodeResolved != null ? { whatsappErrorCode: whatsappErrorCodeResolved } : {}),
    DateCreate: dateStr,
    DateCreatedOriginal: dateStr,
    DateCreateRaw: dateStr,
    ...(formatted !== undefined ? { FormattedMessage: formatted } : {}),
    Reply: (reply as HistoryMessage['Reply']) ?? null,
    reactions: ((m.reaction ?? m.Reaction ?? message.reaction) || null) as string | null,
    Imported: ((m.imported ?? m.Imported ?? message.imported) ?? null) as boolean | null,
    Recovered: ((m.recovered ?? m.Recovered ?? message.recovered) ?? null) as boolean | null,
    IsForwarded: ((m.isForwarded ?? m.IsForwarded ?? message.isForwarded) ?? null) as boolean | null,
    //* שם/מזהה השולח: קודם globalUser* (כותב ההודעה בפועל), ואז manager כ-fallback להודעות בלי כותב.
    ...extractSenderFieldsFromMessagePayload(m),
    StringFormatedMessageWithDate: null,
    WebsitePreviewObj: websitePreviewValue,
    isFavourite: null,
    //* ייחוס לבוט — הודעות/הערות מערכת (Message.BotName) מוצגות "בוט" ולא בשם נציג/משויך.
    BotName: ((m.botName ?? m.BotName) || null) as string | null,
    //* מקור השליחה (broadcast / bot / api...) — לצ'יפ "תפוצה" בבועת ההודעה.
    SendSource: ((m.sendSource ?? m.SendSource ?? m.send_source) || null) as string | null,
    Object: objectValue,
    TempId: null,
  };
}

/** Normalize `Type` on ad-hoc REST rows (search / missing messages) without losing unknown values. */
export function coerceMessageRowTypeField<T extends { Type?: unknown }>(
  msg: T,
): T & { Type?: HistoryMessage['Type'] | string } {
  const next = coerceServerMessageTypeToUi(msg.Type as string);
  return { ...msg, ...(next !== undefined ? { Type: next as HistoryMessage['Type'] } : {}) };
}
